import React, { useMemo } from 'react';
import clsx from 'clsx';
import type {
  FrameworkAgreement,
  RepoMarginCallPreview,
  RepoMarginMethodology,
  TradeItem,
} from '@/types';
import {
  computeMtmRow,
  computeMtmExposure,
  evaluateMtaGate,
  formatCurrency,
  formatCurrencyExact,
} from '@/utils/trades/mtm-calculations';
import {
  getEffectiveSettlementCurrency,
  getSettlementDisplay,
  toSettlementValue,
} from '@/utils/trades/settlement-ccy';
import { aggregateFromBuckets } from '@/utils/trades/currency-totals';
import { displayCashPool, withPoolSign } from '@/utils/trades/cash-pool-display';
import { describeCurrencyTotal } from '@/utils/trades/footer-totals';
import { renderedSections } from '@/utils/trades/agreement-sections';
import { useCurrencyDisplayStore, useFxRatesStore } from '@/stores';
import { CurrencyFilterPill } from '../CurrencyFilterPill';
import { MoneyMetricCell } from '../MoneyMetricCell';
import { ReasonBadge } from '@/components/ui/ReasonBadge';
import { belowMtaReason, resolveMtaCurrency } from '@/utils/trades/mta-display';

export interface MtmAggregateBarProps {
  rows: TradeItem[];
  priceOverrides: Map<number, string>;
  excludedIds: Set<number>;
  /** LR-389: active counterparty's minimum transfer amount; null/undefined = no threshold. */
  mta?: number | null;
  /** ISO currency code for the MTA threshold (default USD). */
  mtaCurrency?: string;
  /**
   * LR-385: the repo margin call for the active counterparty (per-counterparty cash-pool
   * netting from the backend preview). undefined while loading; null when not applicable.
   */
  repoMarginCall?: RepoMarginCallPreview | null;
  repoMarginCallLoading?: boolean;
  /**
   * The counterparty has no GMRA agreement, so its repo book has no call currency. No pool is read in
   * that state (the page gates the preview query), so say why here instead of leaving an unexplained
   * "—" next to a missing Post button — the row-level reason lives in each repo row's checkbox tooltip.
   */
  noGmraAgreement?: boolean;
  /** Open the "post repo margin call" confirmation. Omitted when the user cannot post. */
  onRequestPostRepoMarginCall?: () => void;
  /** True while a repo margin call POST is in flight (disables the button). */
  isPostingRepoMarginCall?: boolean;
  /** LR-479: open the cash-pool posting-history drawer. Omitted → the Cash pool cell is not clickable. */
  onOpenCashPoolHistory?: () => void;
  /**
   * LR-469 currency pill: the currency-pickable basis (subtab rows with every
   * sidebar filter EXCEPT currency) so all currencies stay visible/pickable.
   * When `onCurrenciesChange` is omitted the pill is not rendered.
   */
  currencyPickableRows?: TradeItem[];
  selectedCurrencies?: string[];
  onCurrenciesChange?: (currencies: string[]) => void;
  /**
   * LR-436 D6 follow-up: the basis for section PRESENCE only (which family blocks
   * render), kept WIDER than `rows`. `rows` is scoped to the MTM-eligible book
   * (active|matured) so the money cells preview the run; but a family's section —
   * and crucially the LR-385 repo cash-pool / margin-call controls inside the Repo
   * block — must NOT vanish just because every trade of that family is currently
   * pending settlement. Pass the full sidebar-filtered set here (all statuses).
   * Defaults to `rows` when a caller does not distinguish the two.
   */
  presenceRows?: TradeItem[];
  /**
   * The active counterparty's framework agreement. GMSLA shows only the Stock L/B
   * section, GMRA only the Repo section; null/undefined (or unknown) ⇒ both.
   */
  agreementType?: FrameworkAgreement | null;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EXPOSURE_TOOLTIP = '+ in our favour, − against us';
// LR-479: exposure is now the daily COB T-1 mark vs the booked contract value (not a live snapshot),
// split from the settled cash pool into its own cell.
const CURRENT_EXPOSURE_TOOLTIP =
  'Current exposure = COB (T-1) margin value − contract margin value. + = due to us, − = due to cp';
// LR-579: under Method B the contract side is the Repurchase Price, so the formula above is only
// half the figure — state the one actually applied rather than leaving a tooltip that can't be
// reconciled against the number beside it.
const CURRENT_EXPOSURE_ACCRUED_TOOLTIP =
  'Current exposure = COB (T-1) margin value − (contract margin value + unpaid accrued repo interest), per Method B. + = due to us, − = due to cp';
// LR-542 (feedback #63): the Net-MC tooltip states the relation in words, not as a signed formula —
// the pool is DISPLAYED billing-signed (flipped vs storage), so "exposure − cash pool" would no
// longer reconcile against the two figures on screen.
const NET_MC_TOOLTIP =
  'Net margin call — the current exposure net of the collateral already posted (the amount to settle on the next call). + = due to us, − = due to cp';
// LR-542 (feedback #63): billing sign convention — flipped vs the stored ledger sign.
const CASHPOOL_TOOLTIP =
  'Settled repo cash pool posted with this counterparty, in the billing convention: − = we hold their cash (we pay interest on it), + = they hold ours (the CP pays us). Click for the posting history.';

// LR-579: the applied repo margin call methodology, shown on Current Exposure (the figure it moves).
const METHODOLOGY_BADGE: Record<RepoMarginMethodology, string> = {
  with_accrued: 'Method B',
  without_accrued: 'Method A',
};

const sectionHeaderBase =
  'text-xs font-semibold uppercase tracking-wide whitespace-nowrap px-2 py-1 rounded';

// ─── MtmMetricCell ───────────────────────────────────────────────────────────

interface MtmMetricCellBaseProps {
  label: string;
  value: string;
  alert?: boolean;
  tooltip?: string;
  /** LR-389: optional pill rendered after the value (e.g. "below MTA"). */
  badge?: string;
  /**
   * LR-579: amber (default) is the attention tone every existing badge uses — a threshold, an
   * outage. `info` is the neutral slate variant for a permanent label that states configuration
   * rather than flagging a problem (the repo margin call methodology).
   */
  badgeTone?: 'warn' | 'info';
}

/**
 * `onClick` (LR-479) makes the cell a button (blue label/value, ▸ affordance) that opens a
 * drill-down — the Cash pool cell's posting-history drawer. A badge with a reason is a button too,
 * so the two exclude each other.
 */
type MtmMetricCellProps = MtmMetricCellBaseProps &
  (
    | { badgeReason?: string; onClick?: undefined }
    | { badgeReason?: undefined; onClick?: () => void }
  );

/**
 * A single-currency netted figure (Current Exposure / Cash pool / Net margin call). Multi-currency
 * bucket totals use the grouped `MoneyMetricCell` instead. When `onClick` is set the cell is a
 * button that drills into a detail view (the Cash pool posting history).
 */
function MtmMetricCell({ label, value, alert = false, tooltip, badge, badgeReason, badgeTone = 'warn', onClick }: MtmMetricCellProps) {
  const clickable = onClick != null;
  const body = (
    <>
      <span
        className={clsx(
          'text-xs font-medium uppercase tracking-wide whitespace-nowrap',
          clickable ? 'text-blue-600' : 'text-slate-500',
        )}
      >
        {label}
        {clickable && <span aria-hidden="true" className="ml-1">▸</span>}
      </span>
      <div className="flex items-center">
        <span
          className={clsx(
            // whitespace-nowrap: keep the value on one line — a signed compact value
            // like "-£2.0K" must never break after the leading "-" (CSS allows a line
            // break after a hyphen-minus), which split it across two rows (LR-387).
            'text-sm font-semibold font-mono tabular-nums transition-opacity whitespace-nowrap',
            alert
              ? 'text-red-600'
              : clickable
                ? 'text-blue-600 underline underline-offset-2'
                : 'text-slate-900',
          )}
        >
          {value}
        </span>
        {alert && (
          // Unlike MoneyMetricCell, `title` here is safe: the cell's own tooltip is also a
          // native `title` (not a JS hover-card), so there is no overlapping hover-card.
          <span
            className="w-2 h-2 rounded-full bg-red-500 motion-safe:animate-pulse inline-block ml-1.5"
            role="img"
            aria-label="Negative exposure — against us"
            title="Negative exposure — against us"
          />
        )}
        {badge && (
          // An empty `title` stops the cell's `title` from popping up over the badge and covering
          // its reason tooltip; the rest of the cell keeps it.
          <span title={badgeReason ? '' : undefined} className="ml-1.5 inline-flex">
            <ReasonBadge
              label={badge}
              reason={badgeReason}
              className={clsx(
                'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap',
                badgeTone === 'info' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700',
              )}
            />
          </span>
        )}
      </div>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={tooltip}
        className="flex items-center gap-2 px-4 py-2.5 rounded cursor-pointer hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
      >
        {body}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 cursor-default" title={tooltip}>
      {body}
    </div>
  );
}

// ─── MtmAggregateBar ─────────────────────────────────────────────────────────

export function MtmAggregateBar({
  rows,
  priceOverrides,
  excludedIds,
  mta,
  mtaCurrency,
  repoMarginCall,
  repoMarginCallLoading = false,
  noGmraAgreement = false,
  onRequestPostRepoMarginCall,
  isPostingRepoMarginCall = false,
  onOpenCashPoolHistory,
  currencyPickableRows,
  selectedCurrencies,
  onCurrenciesChange,
  agreementType,
  presenceRows,
}: MtmAggregateBarProps) {
  // LR-474 r5 #2 / LR-436 r1: presence reads `presenceRows` (the WIDE, all-status
  // sidebar set) not `rows` (narrowed to MTM-eligible per D6) — else a family whose
  // every trade is pending settlement would lose its block, hiding an outstanding
  // repo cash pool. The agreement-vs-presence gate itself lives in renderedSections().
  const presenceBasis = presenceRows ?? rows;
  const { renderStockLB, renderRepo } = useMemo(
    () => renderedSections(agreementType, presenceBasis),
    [agreementType, presenceBasis],
  );
  // Display base for the cross-currency `≈` roll-up (EUR/USD toggle) + the FX
  // signal so the roll-up re-denominates when live rates land (same as the
  // Trades MetricsBar and the MTM table footer).
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  const fxVersion = useFxRatesStore((s) => s.version);

  // Compute per-bucket margin sums (new mark) + per-bucket contract-value sums (frozen).
  // INVARIANT: priceOverrides and excludedIds must be new object references on each logical
  // change (the Zustand store creates new Map/Set on every mutating action — see useMtmStore).
  // Mutating in-place would cause stale aggregates because useMemo uses reference equality.
  // Per-bucket totals SPLIT PER CURRENCY (LR-469). Contract value and new-mark margin
  // are denominated in each trade's own currency, so summing across currencies is
  // meaningless (USD 100k + EUR 100k ≠ 200k) — this mirrors the per-currency split the
  // exposure cells already do (LR-387). Integer-cent accumulation per (bucket, currency).
  const aggregates = useMemo(() => {
    const cv: Record<string, Record<string, number>> = { lent: {}, borrowed: {}, repo: {}, reverse: {} };
    const margin: Record<string, Record<string, number>> = { lent: {}, borrowed: {}, repo: {}, reverse: {} };
    const bucketByType: Record<TradeItem['trade_type'], string> = {
      lend: 'lent',
      borrow: 'borrowed',
      repo: 'repo',
      reverse_repo: 'reverse',
    };
    for (const r of rows) {
      if (excludedIds.has(r.id)) continue;
      const b = bucketByType[r.trade_type];
      // LR-474 (D2): bucket by the EFFECTIVE SETTLEMENT currency with
      // settlement-denominated amounts — value and key move together, matching
      // the currency pill / aggregateByCurrency. Already uppercase via the
      // helper; `|| 'USD'` guards a null currency defensively.
      // Contract value prefers the backend twin (exact booked figure); the
      // recomputed mark converts at the booked fx_rate (same rate family).
      const cvDisplay = getSettlementDisplay(r, 'contract_value');
      if (cvDisplay) {
        const cvCcy = cvDisplay.ccy || 'USD';
        cv[b][cvCcy] = (cv[b][cvCcy] ?? 0) + Math.round(cvDisplay.value * 100);
      }
      const mtmRow = computeMtmRow(r, priceOverrides.get(r.id));
      const marginConv = toSettlementValue(r, Number(mtmRow.recomputedMargin));
      const marginCcy =
        (marginConv.converted ? getEffectiveSettlementCurrency(r) : (r.currency || '').toUpperCase()) || 'USD';
      margin[b][marginCcy] = (margin[b][marginCcy] ?? 0) + Math.round(marginConv.value * 100);
    }
    const toDollars = (m: Record<string, number>): Record<string, number> => {
      const out: Record<string, number> = {};
      for (const c in m) out[c] = m[c] / 100;
      return out;
    };
    return {
      lentCV: toDollars(cv.lent),
      lentMargin: toDollars(margin.lent),
      borrowedCV: toDollars(cv.borrowed),
      borrowedMargin: toDollars(margin.borrowed),
      repoCV: toDollars(cv.repo),
      repoMargin: toDollars(margin.repo),
      reverseCV: toDollars(cv.reverse),
      reverseMargin: toDollars(margin.reverse),
    };
  }, [rows, priceOverrides, excludedIds]);

  // Net exposure figures (two separate readouts, never summed).
  // INVARIANT: same as aggregates above — priceOverrides/excludedIds must be new references
  // on each change (Zustand guarantee); in-place mutation causes stale exposure reads.
  const exposure = useMemo(
    () => computeMtmExposure(rows, priceOverrides, excludedIds),
    [rows, priceOverrides, excludedIds],
  );

  // LR-387: the single scalar MTA gates the exposure slice settling in its currency. A lowercase
  // 'usd' would miss the upper-cased 'USD' bucket and silently drop the "below MTA" badge.
  const mtaCcy = resolveMtaCurrency(mtaCurrency);

  // LR-389: gate the Stock L/B net exposure by the counterparty's MTA. Repo exposure is owned
  // by LR-385 and is NOT gated here. A null MTA leaves the gate inert (no badge).
  // LR-387: the single scalar MTA can only gate ONE currency — the MTA currency.
  // LR-474: buckets are keyed by SETTLEMENT currency (booked-fx converted), so the gate
  // compares the MTA against the exposure of trades SETTLING in the MTA ccy — the margin
  // transfer happens in the settlement ccy (known B3 gap vs the backend's security-ccy
  // grouping, separate ticket).
  // NOTE: this gates the aggregate Stock L/B net exposure — a deliberately different projection
  // from the Run-MTM dialog, which gates the signed net delta over included positions
  // (MtmPage.tsx `dialogGate`). When a run mixes repo positions or currencies the two
  // magnitudes can diverge, so the bar badge and the dialog warning may not agree — expected.
  const stockLBGate = useMemo(
    () => evaluateMtaGate(exposure.stockLBNetByCcy[mtaCcy] ?? 0, mta ?? null),
    [exposure.stockLBNetByCcy, mtaCcy, mta],
  );

  // LR-474 follow-up: roll each per-bucket / exposure per-currency map up into ONE
  // currency-aware total (native with its ISO symbol when single-ccy, `≈ base` when
  // mixed, with the full breakdown in the MoneyMetricCell hover card) — the same rule
  // the Trades MetricsBar and the MTM table footer use, replacing the old one-cell-
  // per-currency fan-out. fxVersion forces a re-roll when live FX lands.
  const totals = useMemo(() => {
    const t = (byCcy: Record<string, number>) =>
      describeCurrencyTotal(aggregateFromBuckets(byCcy, baseCurrency));
    return {
      lentCV: t(aggregates.lentCV),
      lentMargin: t(aggregates.lentMargin),
      borrowedCV: t(aggregates.borrowedCV),
      borrowedMargin: t(aggregates.borrowedMargin),
      repoCV: t(aggregates.repoCV),
      repoMargin: t(aggregates.repoMargin),
      reverseCV: t(aggregates.reverseCV),
      reverseMargin: t(aggregates.reverseMargin),
      lbExposure: t(exposure.stockLBNetByCcy),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fxVersion is the live-FX re-roll trigger
  }, [aggregates, exposure, baseCurrency, fxVersion]);

  return (
    <div data-tour="mtm-aggregate-bar" className="flex items-center border-b border-slate-300 bg-white px-2 shadow-[0_2px_6px_-1px_rgba(0,0,0,0.08),0_1px_3px_-1px_rgba(0,0,0,0.06)]">
      {/* Metrics scroll horizontally in their OWN region so the bar itself stays
          overflow:visible. An `overflow-x-auto` on the bar (as it was before) forces
          overflow-y to `auto` too and clipped the currency pill's absolute Popover panel
          — headlessui v1 doesn't portal it — so the dropdown opened INSIDE the bar with a
          stray vertical scrollbar. The pill now lives OUTSIDE this region (below); the
          MoneyMetricCell hover cards portal to <body>, so nothing else needs the wrapper.
          The Trades MetricsBar uses the same layout. */}
      <div className="scroll-x-thin flex min-w-0 flex-1 items-center overflow-x-auto">
        {/* Stock L/B section — collapsed for a GMRA-only CP UNLESS it carries
            stock-lend/borrow rows (LR-474 r5 #2 — never hide extant trades). */}
        {renderStockLB && (
          <div className="flex items-center" role="group" aria-label="Stock Lend/Borrow metrics">
            <span className={clsx(sectionHeaderBase, 'text-slate-500')}>Stock L/B</span>
            <MoneyMetricCell label="Lent Value" total={totals.lentCV} />
            <MoneyMetricCell label="Lent Margin" total={totals.lentMargin} />
            <MoneyMetricCell label="Borr. Value" total={totals.borrowedCV} />
            <MoneyMetricCell label="Borr. Margin" total={totals.borrowedMargin} />
            {/* LR-474: one rolled-up exposure readout (breakdown in the hover card), never
                summed in a misleading native way. The single scalar MTA gates the slice in
                the MTA currency; its "below MTA" badge attaches here — but only when that
                currency actually has an L/B exposure slice. With no MTA-ccy exposure there is
                nothing to transfer, so the badge is suppressed even though the empty 0 slice
                is technically below the threshold (matches the pre-LR-474 per-currency rule). */}
            {(() => {
              const showLbBadge = stockLBGate.belowMta && mtaCcy in exposure.stockLBNetByCcy;
              return (
                <MoneyMetricCell
                  label="L/B Exposure"
                  total={totals.lbExposure}
                  alert={totals.lbExposure.value < 0}
                  formula={EXPOSURE_TOOLTIP}
                  badge={showLbBadge ? 'below MTA' : undefined}
                  badgeTooltip={
                    showLbBadge && stockLBGate.mta != null
                      ? belowMtaReason(stockLBGate.mta, mtaCcy)
                      : undefined
                  }
                />
              );
            })()}
          </div>
        )}

        {/* Vertical separator — only when both sections are shown. */}
        {renderStockLB && renderRepo && (
          <span aria-hidden="true" className="mx-2 h-6 w-px bg-slate-300 select-none" />
        )}

        {/* Repo section — collapsed for a GMSLA-only CP UNLESS it carries
            repo/reverse-repo rows (LR-474 r5 #2 — never hide extant trades). */}
        {renderRepo && (
          <div className="flex items-center" role="group" aria-label="Repo metrics">
            <span className={clsx(sectionHeaderBase, 'text-slate-500')}>Repo</span>
            <MoneyMetricCell label="Repo Value" total={totals.repoCV} />
            <MoneyMetricCell label="Repo Margin" total={totals.repoMargin} />
            <MoneyMetricCell label="Rev. Value" total={totals.reverseCV} />
            <MoneyMetricCell label="Rev. Margin" total={totals.reverseMargin} />
            {/* LR-385/LR-479: repo/reverse contracts are NOT repriced — the bucket shows three
                separate single-currency figures from the backend preview: Current Exposure (the daily
                COB T-1 mark vs the booked contract value), the settled Cash pool (clickable → posting
                history; LR-542 displays it billing-signed, i.e. flipped vs the stored value), and the
                Net margin call (exposure net of the pool, the amount to settle next). */}
            {(() => {
              // No GMRA agreement → no call currency, no pool was read, and posting is off. Name the
              // reason where the Post button would have been.
              if (noGmraAgreement) {
                return (
                  <MtmMetricCell
                    label="Net margin call"
                    value="—"
                    tooltip={NET_MC_TOOLTIP}
                    badge="no GMRA agreement"
                    badgeReason="This counterparty has no GMRA agreement, so its repo book has no currency to be margined in — the cash pool is not read and posting is unavailable. Add the agreement in the counterparty registry."
                  />
                );
              }
              if (repoMarginCallLoading) {
                return <MtmMetricCell label="Net margin call" value="…" tooltip={NET_MC_TOOLTIP} />;
              }
              if (!repoMarginCall) {
                return <MtmMetricCell label="Net margin call" value="—" tooltip={NET_MC_TOOLTIP} />;
              }
              const netDue = Number(repoMarginCall.net_due);
              const exposure = Number(repoMarginCall.total_exposure);
              const pool = Number(repoMarginCall.cashpool_before);
              // The book's collateral sits in a pool of another currency than the
              // GMRA agreement's, so it couldn't be netted. The exposure above is honest, but the pool
              // wasn't netted — show it as unavailable (not a fake 0) and block posting.
              const fxMismatch = repoMarginCall.fx_currency_mismatch ?? false;
              // The figures are in the GMRA agreement currency.
              const callCcy = repoMarginCall.settlement_currency || repoMarginCall.currency;
              // The preview gives the MTA in the call currency at COB, as the collateral queue does.
              const callMta = repoMarginCall.minimum_transfer_amount;
              const gate = evaluateMtaGate(netDue, callMta == null ? null : Number(callMta));
              const dirLabel =
                repoMarginCall.direction === 'due_to_cp'
                  ? 'Due to CP'
                  : repoMarginCall.direction === 'due_to_us'
                    ? 'Due to us'
                    : 'Settled';
              // Feedback #62: sign the figure — "Due to CP -$1.0M", not a red "+$1.0M". formatCurrency
              // renders the "-" for negatives; a settled (zero) call shows no figure at all.
              const mcValue =
                repoMarginCall.direction === 'none'
                  ? 'Settled'
                  : `${dirLabel} ${formatCurrency(netDue, callCcy)}`;
              // LR-579: the methodology the backend computed this exposure under — the counterparty's
              // election, so it belongs on the figure it changes. This cell carries no warning badge
              // (those live on Net margin call), so the label never displaces one.
              const methodology = repoMarginCall.methodology;
              const accruedPart = Number(repoMarginCall.accrued_component ?? '0');
              return (
                <>
                  <MtmMetricCell
                    label="Current Exposure"
                    value={formatCurrency(exposure, callCcy)}
                    tooltip={
                      methodology === 'with_accrued'
                        ? CURRENT_EXPOSURE_ACCRUED_TOOLTIP
                        : CURRENT_EXPOSURE_TOOLTIP
                    }
                    badge={methodology ? METHODOLOGY_BADGE[methodology] : undefined}
                    badgeTone="info"
                    badgeReason={
                      methodology === 'with_accrued'
                        ? `Method B (price plus accrued) — this exposure includes ${formatCurrencyExact(accruedPart, callCcy)} of repo interest accrued and not yet paid${repoMarginCall.accrued_as_of ? `, through ${repoMarginCall.accrued_as_of}` : ''}. Set on the counterparty.`
                        : methodology === 'without_accrued'
                          ? 'Method A — the price move only; accrued repo interest is excluded from the exposure. Set on the counterparty.'
                          : undefined
                    }
                  />
                  {/* LR-542 (feedback #63): the pool is displayed billing-signed — a call due to us
                      DECREASES the shown pool. Flip at render only; `pool` itself stays stored-signed.
                      withPoolSign adds the explicit "+" on positives (the sign IS the information). */}
                  <MtmMetricCell
                    label="Cash pool"
                    value={
                      fxMismatch
                        ? '—'
                        : withPoolSign(
                            displayCashPool(pool),
                            formatCurrency(displayCashPool(pool), callCcy)
                          )
                    }
                    tooltip={CASHPOOL_TOOLTIP}
                    onClick={onOpenCashPoolHistory}
                  />
                  <MtmMetricCell
                    label="Net margin call"
                    value={fxMismatch ? '—' : mcValue}
                    alert={!fxMismatch && netDue < 0}
                    tooltip={NET_MC_TOOLTIP}
                    badge={
                      fxMismatch ? 'pool in another currency' : gate.belowMta ? 'below MTA' : undefined
                    }
                    badgeReason={
                      fxMismatch
                        ? 'The collateral sits in a cash pool of another currency than the GMRA agreement, so it could not be netted. Showing the current exposure only. If the collateral is all in one currency, change the GMRA agreement to it in the counterparty registry to post.'
                        : gate.belowMta && gate.mta != null
                          ? belowMtaReason(gate.mta, callCcy)
                          : undefined
                    }
                  />
                  {onRequestPostRepoMarginCall && (
                    <button
                      type="button"
                      onClick={onRequestPostRepoMarginCall}
                      disabled={isPostingRepoMarginCall || fxMismatch}
                      className={clsx(
                        'ml-1 rounded px-2.5 py-1 text-xs font-semibold whitespace-nowrap transition-colors',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500',
                        isPostingRepoMarginCall || fxMismatch
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer',
                      )}
                      title={
                        fxMismatch
                          ? 'Posting is disabled: the collateral sits in a cash pool of another currency than the GMRA agreement. If it is all in one currency, change the GMRA agreement to it in the counterparty registry.'
                          : 'Post this repo margin call (prefilled with the computed amount; editable before posting)'
                      }
                    >
                      {isPostingRepoMarginCall ? 'Posting…' : 'Post margin call'}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Currency pill — a sibling OUTSIDE the scrolling metrics region so its Popover
          panel is never clipped, and pinned right (never needs a horizontal scroll to
          reach) as the flex-1 metrics region pushes it over. */}
      {onCurrenciesChange && (
        <CurrencyFilterPill
          className="ml-2 shrink-0"
          rows={currencyPickableRows ?? rows}
          selected={selectedCurrencies ?? []}
          onChange={onCurrenciesChange}
          excludedIds={excludedIds}
        />
      )}
    </div>
  );
}
